import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import AudioPlayer from '../../../components/AudioPlayer';

const GlossaryEntryCard = ({ entry, onEdit, onDelete, onAudioUpload, onPlayAudio, onViewUsage, onRetryAudio }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedWord, setEditedWord] = useState(entry?.word);
  const [editedDefinition, setEditedDefinition] = useState(entry?.definition);
  const [editedPronunciation, setEditedPronunciation] = useState(entry?.pronunciation);
  const [isPlaying, setIsPlaying] = useState(false);
  const fileInputRef = useRef(null);

  const handleSave = () => {
    onEdit(entry?.id, {
      word: editedWord,
      definition: editedDefinition,
      pronunciation: editedPronunciation
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedWord(entry?.word);
    setEditedDefinition(entry?.definition);
    setEditedPronunciation(entry?.pronunciation);
    setIsEditing(false);
  };

  const handleAudioUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      onAudioUpload(entry?.id, file);
    }
  };

  const handlePlayAudio = () => {
    setIsPlaying(true);
    onPlayAudio(entry?.audioUrl);
    setTimeout(() => setIsPlaying(false), 2000);
  };

  const getStatusColor = () => {
    if (entry?.definition && entry?.audioUrl) return 'text-success';
    if (entry?.definition && !entry?.audioUrl) return 'text-warning';
    return 'text-error';
  };

  const getStatusIcon = () => {
    if (entry?.definition && entry?.audioUrl) return 'CheckCircle';
    if (entry?.definition && !entry?.audioUrl) return 'AlertCircle';
    return 'XCircle';
  };

  const getStatusText = () => {
    if (entry?.definition && entry?.audioUrl) return 'Complete';
    if (entry?.definition && !entry?.audioUrl) return 'Missing Audio';
    return 'Incomplete';
  };

  const getAudioStatusBadge = () => {
    const status = entry?.audioGenerationStatus;
    if (status === 'completed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
          <Icon name="Volume2" size={10} />
          Audio Ready
        </span>
      );
    }
    if (status === 'generating') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
          <Icon name="Loader" size={10} className="animate-spin" />
          Generating...
        </span>
      );
    }
    if (status === 'failed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
          <Icon name="AlertTriangle" size={10} />
          Audio Failed
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
          <Icon name="Clock" size={10} />
          Audio Pending
        </span>
      );
    }
    return null;
  };

  const getQualityFlagBadge = () => {
    const flag = entry?.audioQualityFlag;
    if (flag === 'ai_generated') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
          <Icon name="Sparkles" size={10} />
          AI Generated
        </span>
      );
    }
    if (flag === 'approved') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
          <Icon name="ShieldCheck" size={10} />
          Approved
        </span>
      );
    }
    return null;
  };

  const showRetryButton =
    (entry?.audioGenerationStatus === 'failed' || entry?.audioGenerationStatus === 'pending') &&
    typeof onRetryAudio === 'function';

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          {isEditing ? (
            <Input
              type="text"
              value={editedWord}
              onChange={(e) => setEditedWord(e?.target?.value)}
              className="mb-2"
              label="Archaic Word"
            />
          ) : (
            <h3 className="text-xl font-bold text-foreground mb-1">{entry?.word}</h3>
          )}
          {isEditing ? (
            <Input
              type="text"
              value={editedPronunciation}
              onChange={(e) => setEditedPronunciation(e?.target?.value)}
              className="mb-2"
              label="Pronunciation Guide"
              placeholder="e.g., /ˈθaʊ/"
            />
          ) : (
            entry?.pronunciation && (
              <p className="text-sm text-muted-foreground data-text">
                {entry?.pronunciation}
              </p>
            )
          )}
        </div>
        <div className={`flex items-center gap-2 ${getStatusColor()}`}>
          <Icon name={getStatusIcon()} size={20} />
          <span className="text-sm font-medium">{getStatusText()}</span>
        </div>
      </div>

      {isEditing ? (
        <textarea
          value={editedDefinition}
          onChange={(e) => setEditedDefinition(e?.target?.value)}
          className="w-full min-h-24 px-4 py-3 bg-background border border-border rounded-md text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary mb-4"
          placeholder="Enter modern English definition..."
        />
      ) : (
        <p className="text-foreground mb-4 leading-relaxed">
          {entry?.definition || (
            <span className="text-muted-foreground italic">No definition provided</span>
          )}
        </p>
      )}

      {/* Audio status and quality badges */}
      {(entry?.audioGenerationStatus || entry?.audioQualityFlag) && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {getAudioStatusBadge()}
          {entry?.audioGenerationStatus === 'completed' && getQualityFlagBadge()}
        </div>
      )}

      {/* AI-generated pending review notice */}
      {entry?.aiGenerated && entry?.reviewStatus === 'pending_review' && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md mb-4">
          <Icon name="AlertCircle" size={14} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700 dark:text-amber-300">
            AI-generated definition — pending admin review before publishing.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {entry?.playTitle && (
          <span
            className="px-3 py-1 bg-secondary/10 text-secondary text-xs font-medium rounded-full"
          >
            {entry?.playTitle}
          </span>
        )}
        <button
          onClick={() => onViewUsage?.(entry)}
          className="px-3 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full hover:bg-accent/20 transition-colors cursor-pointer flex items-center gap-1"
        >
          <Icon name="BarChart2" size={12} />
          {entry?.totalUsageCount || 0} {entry?.totalUsageCount === 1 ? 'usage' : 'usages'}
        </button>
      </div>
      
      {entry?.audioUrl && (
        <div className="mb-4">
          <AudioPlayer 
            audioUrl={entry?.audioUrl}
            label={`Play pronunciation of "${entry?.word}"`}
            size="sm"
            variant="outline"
            showDuration={false}
            onPlayStart={() => setIsPlaying(true)}
            onPlayEnd={() => setIsPlaying(false)}
          />
        </div>
      )}
      
      <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-border">
        {isEditing ? (
          <>
            <Button variant="default" size="sm" onClick={handleSave}>
              Save Changes
            </Button>
            <Button variant="outline" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
            {!entry?.audioUrl && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Upload"
                  iconPosition="left"
                  onClick={() => fileInputRef?.current?.click()}
                >
                  Upload Audio
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/mp3,audio/mpeg,audio/wav"
                  onChange={handleAudioUpload}
                  className="hidden"
                />
              </>
            )}
            {showRetryButton && (
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => onRetryAudio(entry?.id, entry?.word)}
              >
                Retry Audio
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              onClick={() => onDelete(entry?.id)}
            >
              Delete
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default GlossaryEntryCard;